//Services
import { format } from 'svelte-i18n';
import { postReq } from '../../services/http';
//Helpers
import { get_ip, get_local_ip } from '../../helpers/interface_helpers';
import { save_actions } from '../DevicesSettings/WebHooks/WebHooksHelpers/get_actions';
//Stores
import { pending_commands } from 'src/stores/device-runtime/process';
import { devices_settings } from 'src/stores/device-runtime/status';
import { readDeviceSettings, readDeviceStatus } from 'src/stores/device-runtime/status-selectors';
//Design system components
import { toast } from '@Shelly/shelly-design-system';
import { get } from 'svelte/store';
import { gen2Request } from '../../services/gen_2_http_reqs';

/**
 * @function clearActionsUrls we collect all
 * @param actions and remove its url addresses
 * @returns all actions with removed addresses
 */
export const clearActionsUrls = (actions) => {
	const cleared_actions = {};
	Object.entries(actions).forEach((action: any) => {
		if (action[0] !== 'category' && action[0] !== 'ips' && action[0] !== 'isok') {
			//those two actions are used only to transport data, so they doesn't have urls
			action[1].urls = [];
			action[1].enabled = false;
		}
		cleared_actions[action[0]] = action[1];
	});
	return cleared_actions;
};

/**
 * @function sendWindowReport simply sends window report request
 * @param device the trv's device object
 * @param settings the trv's settings object
 */
const sendWindowReport = async (device, settings) => {
	const _format = get(format);
	const res = await postReq(
		`/device/generic_command`,
		{
			id: device.id,
			command: `open_window_report`,
			payload: JSON.stringify({
				id: device.channel,
				enabled: true,
			}),
		},
		20000,
	);
	if (res.isok) {
		settings['thermostats'][0].open_window_report = true;
		devices_settings.update_devices_settings(device.id, settings);
		toast.open({ type: 'success', title: _format('setting_scc_saved_toast') });
	} else {
		toast.open({ type: 'danger', title: _format('error_global') });
	}
};
/**
 * @function setOpenWindowReport is just a toggler before sending the request
 */
const setOpenWindowReport = async (device, settings) => {
	let enabledReport = settings.thermostats[0].open_window_report;
	if (!enabledReport) {
		sendWindowReport(device, settings);
	}
};
/**
 * @function saveAction is just a save_actions caller used in @component QuickActions. Abstraction which maybe us pointless to call two times the toast
 * @param deviceId
 * @param condition selected condition
 * @param _actions all actions
 * @returns response to use then
 */
export const saveAction = async (deviceId, condition, _actions, devices) => {
	const _format = get(format);
	const res = await save_actions(
		devices[deviceId],
		true,
		condition,
		_actions[condition].urls,
		'',
		0,
		get_local_ip(deviceId),
	);
	if (res.isok) {
		// const pending_status = get(pending_commands)[deviceId];
		// if (pending_status) {
		// 	toast.open('info', get(format)('info_apply_setting_on_wake_up'));
		// } else {
		// 	toast.open('success', get(format)('setting_scc_saved_toast'));
		// }
	} else {
		toast.open({ type: 'danger', title: _format('setting_err_toast') });
	}
	return res;
};

/**
 * @function saveExtT enable trv's external temperature measurement setting
 * @param _device trv's device object
 */
export const saveExtT = async (device, settings, devices) => {
	const _format = get(format);
	const ext_t = settings.thermostats[0].ext_t.enabled;
	if (!ext_t) {
		const res = await postReq(
			`/device/generic_command`,
			{
				id: device.id,
				command: `ext_t`,
				payload: JSON.stringify({
					enabled: true,
					id: device.channel,
				}),
			},
			20000,
		);

		if (res.isok) {
			settings.thermostats[0].ext_t.enabled = true;
			devices_settings.update_devices_settings(device.wid, settings);
			// toast.open('success', $format('setting_scc_saved_toast'));
		} else {
			toast.open({ type: 'danger', title: _format('setting_err_toast') });
		}
	}
};
/**
 * @function handleSingleSensorSaveActions based on window report sends one or more action requests
 * @param device the device object
 * @param devices the devices store
 * @param selectedDevices selected devices array
 * @param actions updated actions
 * @param windowReport window report boolean
 * @param _condition current action
 * @returns fake isok to stop loading
 */
export const handleSingleSensorSaveActions = async (
	device,
	devices,
	selectedDevices,
	actions,
	windowReport,
	_condition = 'open',
	type = 'old',
) => {
	const sensor_id = selectedDevices[0]; //selected sensor
	const hostSettings = readDeviceSettings(device.id);
	const hostStatus = readDeviceStatus(device.id);
	const _actions =
		typeof actions[sensor_id] !== 'undefined' ? clearActionsUrls(actions[sensor_id]) : []; //removing all urls
	const _format = get(format);
	if (!windowReport) {
		let res;
		let url: { url: string; int: string[] } | string = `http://${
			device.ip || get_ip(hostStatus) //this is needed to get sleeping and half sleeping devices ips
		}/ext_t`;
		if (type !== 'newHooks') {
			const condition = Object.entries(actions[sensor_id]).filter((action) =>
				action[0].includes('report'),
			)[0][0];
			if (type === 'newActions') {
				_actions[condition].urls.push({
					url,
					int: '0000-0000',
				});
			} else {
				_actions[condition].urls.push(url);
			}
			res = await save_actions(
				devices[sensor_id],
				true,
				condition,
				_actions[condition].urls,
				'',
				'',
				get_local_ip(sensor_id),
			);
			if (res.isok) {
				saveExtT(device, hostSettings, devices);
			}
		} else {
			const _device = devices[sensor_id];
			const webHook = {
				cid: _device.channel,
				event: 'temperature.change',
				enable: true,
				active_between: ['00:00', '00:00'],
				name: `Temperature report to ${devices[device.id].name}`,
				urls: [url + '?temp=${ev.tC}'],
				ssl_ca: 'ca.pem',
			};
			res = await gen2Request(_device, 'webhook.create', webHook);
		}
		if (res.isok) {
			const pendingStatus = get(pending_commands)[sensor_id];
			if (pendingStatus) {
				toast.open({
					type: 'info',
					title: _format('info_apply_setting_on_wake_up'),
				});
			} else {
				toast.open({
					type: 'success',
					title: _format('setting_scc_saved_toast'),
				});
			}
		} else {
			toast.open({ type: 'danger', title: _format('setting_err_toast') });
		}
		return res;
	} else {
		const conditions: [string, any][] = Object.entries(actions[sensor_id]).filter(
			(action) => action[0] !== 'isok',
		);
		setOpenWindowReport(device, hostSettings);
		let res, res1;
		for (let [condition, value] of conditions) {
			const ip = device.ip || get_ip(hostStatus); //this is needed because dw devices are sleeping devices;
			if (condition.includes(_condition)) {
				value.enabled = 1;
				value.urls.push(`http://${ip}/window?state=open`);
				res = await saveAction(sensor_id, condition, _actions, devices);
			}
			if (condition.includes('close')) {
				value.enabled = 1;
				value.urls.push(`http://${ip}/window?state=close`);
				res1 = await saveAction(sensor_id, condition, _actions, devices);
			}
		}
		Promise.all([res, res1]).then((res) => {
			if (res[0].isok && res[1].isok) {
				const pendingStatus = get(pending_commands)[sensor_id];
				if (pendingStatus) {
					toast.open({
						type: 'info',
						title: _format('info_apply_setting_on_wake_up'),
					});
				} else {
					toast.open({
						type: 'success',
						title: _format('setting_scc_saved_toast'),
					});
				}
			} else {
				toast.open({ type: 'danger', title: _format('setting_err_toast') });
			}
		});
		return { isok: true };
	}
};
